// Opus review of agent specs (migration 20261024200000). Security Audit specs are written on Grok; when one lands in
// spec_ready the queue listener (workspace scripts/queue-breaker-poke.mjs) runs a review turn on Opus. The reviewer
// checks the spec against the live system, then `cockpit ai review` records one of three outcomes:
//   pass   — every decision answered (Kyle can change any of them), review_state 'passed'. Kyle still approves.
//   revise — back to the spec agent with a note, review_state cleared so the next version is reviewed too.
//   stall  — review_state 'stalled': three reviews without a pass, or a problem the spec agent can't fix.
// Kyle sending a passed or stalled spec back himself starts a fresh cycle (trigger ai_jobs_review_reset).

import type { BadgeTone } from '../components/ui/Badge';
import type { AiJob } from './supabase.types';

/** Task types whose specs get an Opus review. Keep in step with REVIEW_TASK_TYPES in the queue listener. */
export const REVIEWED_TASK_TYPES: ReadonlyArray<AiJob['task_type']> = ['security_audit'];

/** Reviews per cycle before the job stalls for Kyle. The database refuses a fourth. */
export const MAX_REVIEW_ROUNDS = 3;

export const isReviewedTaskType = (taskType: AiJob['task_type']) => REVIEWED_TASK_TYPES.includes(taskType);

export type SpecReviewBadge = { label: string; tone: BadgeTone; title: string; pulse?: boolean };

/** The review state as a badge, or null when this job has nothing to show. */
export function specReviewBadge(job: Pick<AiJob, 'status' | 'task_type' | 'review_state' | 'review_round' | 'review_note'>): SpecReviewBadge | null {
  if (!isReviewedTaskType(job.task_type)) return null;
  const round = job.review_round ?? 0;
  const note = job.review_note?.trim() || '';
  switch (job.review_state) {
    case 'reviewing':
      return { label: 'Opus reviewing', tone: 'info', pulse: true, title: `Opus is checking this spec (review ${round + 1} of ${MAX_REVIEW_ROUNDS}).` };
    case 'passed':
      return { label: 'Opus reviewed', tone: 'up', title: note || 'Opus checked this spec and picked every answer. Change any you disagree with, then approve.' };
    case 'stalled':
      return { label: 'Review stalled', tone: 'warn', title: note || 'Opus could not pass this spec. It needs you.' };
    default:
      if (job.status === 'spec_ready') return { label: 'Opus review due', tone: 'neutral', title: 'The queue listener starts an Opus review shortly.' };
      if (round > 0 && (job.status === 'spec_revision' || job.status === 'spec_in_progress')) {
        return { label: `Opus sent back ${round}/${MAX_REVIEW_ROUNDS}`, tone: 'warn', title: note };
      }
      return null;
  }
}
